import { Box, Typography } from "@mui/material"
import type React from "react"
import { dayjsUTC } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import type { NwssEventWindow } from "./nwssEventWindow"
import { NwssWeeklyEventWindowMarkers } from "./NwssWeeklyEventWindowBand"
import {
  isNwssWeekMonthStart,
  NWSS_WEEK_GRID_ROW_LABEL_WIDTH_PX,
  weekColumnLeftPercent,
} from "./nwssWeeklyGridLayout"

export interface NwssWeeklyChartFrameProps {
  weekKeys: string[]
  eventWindow?: NwssEventWindow | null
  showEventWindowBand?: boolean
  children: React.ReactNode
}

/** Header track above the compact weekly grid. */
export const NwssWeeklyChartFrame: React.FC<NwssWeeklyChartFrameProps> = ({
  weekKeys,
  eventWindow = null,
  showEventWindowBand = false,
  children,
}) => {
  const markersVisible = showEventWindowBand && eventWindow != null

  return (
    <Box sx={{ width: "100%" }}>
      <NwssWeeklyTimelineHeader weekKeys={weekKeys} />
      <Box sx={{ position: "relative", width: "100%" }}>
        {markersVisible && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              gap: "2px",
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            <Box
              sx={{
                width: NWSS_WEEK_GRID_ROW_LABEL_WIDTH_PX,
                flexShrink: 0,
              }}
            />
            <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
              <NwssWeeklyEventWindowMarkers
                weekKeys={weekKeys}
                eventWindow={eventWindow}
              />
            </Box>
          </Box>
        )}
        <Box sx={{ position: "relative", zIndex: 1 }}>{children}</Box>
      </Box>
    </Box>
  )
}

export interface NwssWeeklyTimelineHeaderProps {
  weekKeys: string[]
}

const NwssWeeklyTimelineHeader: React.FC<NwssWeeklyTimelineHeaderProps> = ({
  weekKeys,
}) => {
  const total = weekKeys.length
  if (total === 0) return null

  return (
    <Box sx={{ display: "flex", gap: "2px", width: "100%", mb: 0.25 }}>
      <Box sx={{ width: NWSS_WEEK_GRID_ROW_LABEL_WIDTH_PX, flexShrink: 0 }} />
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          minHeight: 14,
        }}
      >
        {weekKeys.map((weekKey, index) =>
          isNwssWeekMonthStart(weekKeys, index) ? (
            <Typography
              key={weekKey}
              sx={{
                position: "absolute",
                left: `${weekColumnLeftPercent(index, total)}%`,
                bottom: 0,
                fontSize: "0.55rem",
                color: extraColors.translucent50,
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              {dayjsUTC(weekKey).format("MMM")}
            </Typography>
          ) : null
        )}
      </Box>
    </Box>
  )
}
